<template>
	<div class="totp-verification-container">
		<div class="verification-card">
			<!-- Header -->
			<div class="card-header">
				<h4 class="mb-0 text-uppercase font-weight-bold">
					{{ $t('page.twoFactorAuth.totpVerificationTitle') }}
				</h4>
			</div>

			<!-- Body -->
			<div class="card-body">
				<div class="text-center">
					<!-- Icon -->
					<div class="mb-2">
						<b-icon icon="shield-lock" font-scale="3" variant="primary" class="mb-3"></b-icon>
					</div>

					<!-- Instructions -->
					<div class="mb-4">
						<p class="mb-2 font-weight-bold label">{{ $t('page.twoFactorAuth.totpVerificationInstruction') }}</p>
						<!-- <small class="text-muted">
							{{ $t('totpVerificationHelp') }}
						</small> -->
					</div>

					<!-- Verification Input -->
					<div class="mb-4">
						<b-form-input v-model="verificationCode" maxlength="6"
							class="text-center verification-input" :class="{ 'is-invalid': hasError }"
							@input="validateCode" @keyup.enter="verifyCode" autofocus>
						</b-form-input>
						<small v-if="!errorMessage" class="text-muted">
							{{ $t('page.twoFactorAuth.totpVerificationHelpText') }}
						</small>
						<small v-else class="text-danger">
							{{ $t(errorMessage) || errorMessage }}
						</small>
					</div>

					<!-- Action Buttons -->
					<div class="mb-3">
						<b-button variant="none" style="color:#fff"
							class="justify-content-center align-items-center bold emotiva-pink-background action-btn mb-2"
							:disabled="isFormInvalid || isVerifying" @click="verifyCode">
							<b-icon v-if="isVerifying" icon="arrow-clockwise" animation="spin" class="mr-2"></b-icon>
							{{ isVerifying ? ($t('button.verifying') || 'Verifying...') : ($t('button.verify') ||
							'Verify') }}
						</b-button>
					</div>
				</div>
			</div>
		</div>

		<!-- Remember Device Option (Optional) -->
		<!-- <div class="remember-device-card mt-3" v-if="showRememberDevice">
			<b-form-checkbox 
				v-model="rememberDevice" 
				class="text-center">
				<small class="text-muted">
					{{ $t('totpVerificationRememberDevice') || 'Remember this device for 30 days' }}
				</small>
			</b-form-checkbox>
		</div> -->
	</div>
</template>

<script>
export default {
	name: 'TotpVerificationPage',
	data() {
		return {
			verificationCode: '',
			errorMessage: '',
			isVerifying: false,
			rememberDevice: false,
			showRememberDevice: true // Set to false if you don't want this feature
		}
	},
	computed: {
		isFormInvalid() {
			return !this.verificationCode || this.verificationCode.length !== 6 || !/^\d{6}$/.test(this.verificationCode);
		},
		hasError() {
			return !!this.errorMessage;
		}
	},
	mounted() {
		// Auto-focus on the input when page loads
		this.$nextTick(() => {
			const input = this.$el.querySelector('input');
			if (input) {
				input.focus();
			}
		});
	},
	methods: {
		validateCode() {
			// Clear any previous error
			this.errorMessage = '';

			// Remove any non-numeric characters
			this.verificationCode = this.verificationCode.replace(/\D/g, '');

			// Limit to 6 digits
			if (this.verificationCode.length > 6) {
				this.verificationCode = this.verificationCode.substring(0, 6);
			}
		},

		async verifyCode() {
			if (this.isFormInvalid || this.isVerifying) return;

			this.isVerifying = true;
			this.errorMessage = '';
			let redirectRoute = ''
			try {
				this.$store.commit("loader/SET_LOADER", {});

				const response = await this.$store.dispatch('authTotpVerification/validateUserTotpAndLogin', {
					totp: this.verificationCode,
				});
				this.$store.commit("auth/SET_DATA_AFTER_LOGIN", response)
				await this.$store.dispatch("auth/retrieveClient");

				this.$store.commit("auth/SET_IS_LOGGED_IN", true);

				// set up subscription after login
				this.$store.dispatch("subscription/retrieveActiveSubscriptionByClientId")
					.then((response) => {
						this.activeSubscription = response;
					})
					.catch((e) => console.error('Something went wrong when fetching subscription: ', e))

				// this.$store.commit("settings/SET_TOUR_ACTIVE", this.isFirstLogin);

				redirectRoute = this.$route.query.redirect;
				if (redirectRoute) {
					this.$router.replace(redirectRoute);
				} else {
					this.$router.replace({ name: "Dashboard" });
				}
			} catch (error) {
				console.error("Error verifying TOTP: ", error);

				// Handle different error types
				if (error.response?.status === 401) {
					this.errorMessage = 'page.twoFactorAuth.2FAWrongTOPT';
				} else if (error.response?.status === 429) {
					this.errorMessage = 'page.twoFactorAuth.totpVerificationTooManyAttempts';
				} else {
					this.errorMessage = 'page.twoFactorAuth.2FAWrongTOPT';
				}

				// Clear the input on error
				this.verificationCode = '';

				// Refocus input
				this.$nextTick(() => {
					const input = this.$el.querySelector('input');
					if (input) {
						input.focus();
					}
				});

			} finally {
				this.isVerifying = false;
				this.$store.dispatch("loader/close");
			}
		},

		resendCode() {
			// If you have backup codes or alternative verification methods
			// This could trigger sending an SMS backup code, etc.
			this.$bvToast.toast(
				this.$t('totpVerificationResendMessage') || 'Please check your authenticator app for the current code.',
				{
					title: this.$t('totpVerificationResendTitle') || 'Code Refresh',
					variant: 'info',
					solid: true,
					autoHideDelay: 5000
				}
			);
		},

		logout() {
			// Clear any stored authentication data and redirect to login
			this.$store.dispatch('auth/logout').then(() => {
				this.$router.push('/login');
			});
		}
	}
}
</script>

<style scoped>
.totp-verification-container {
	min-height: 70vh;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background-color: #f8f9fa;
	padding: 20px;
}

.verification-card {
	background: white;
	border-radius: 16px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	width: 100%;
	max-width: 400px;
}

.card-header {
	padding: 24px 24px 0;
	text-align: center;
}

.card-body {
	padding: 24px;
}

.verification-input {
	letter-spacing: 0.3em;
	font-size: 1.2em;
	font-weight: 600;
	height: 50px;
	border-radius: 8px;
	border: 2px solid #e9ecef;
	transition: all 0.2s ease;
}

.verification-input:focus {
	border-color: var(--emotiva-pink, #e91e63);
	box-shadow: 0 0 0 0.2rem rgba(233, 30, 99, 0.25);
}

.verification-input.is-invalid {
	border-color: #dc3545;
}

.action-btn {
	border-radius: 26px;
	height: 46px;
	min-width: 120px;
	width: 100%;
	font-weight: 600;
	transition: all 0.2s ease;
}

.action-btn:disabled {
	opacity: 0.7;
	cursor: not-allowed;
}

.emotiva-pink-background {
	background-color: var(--emotiva-pink, #e91e63);
}

.emotiva-pink-background:hover:not(:disabled) {
	background-color: var(--emotiva-pink-dark, #c2185b);
	transform: translateY(-1px);
}

.label {
	color: var(--emotiva-text-color, #333);
}

.alternative-actions {
	margin-top: 20px;
}

.alternative-actions .btn-link {
	text-decoration: none;
	font-size: 0.85em;
}

.alternative-actions .btn-link:hover {
	text-decoration: underline;
}

.remember-device-card {
	background: white;
	border-radius: 12px;
	padding: 16px;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
	width: 100%;
	max-width: 400px;
}

/* Responsive Design */
@media (max-width: 480px) {
	.totp-verification-container {
		padding: 15px;
	}

	.verification-card {
		border-radius: 12px;
	}

	.card-header,
	.card-body {
		padding: 20px;
	}

	.verification-input {
		font-size: 1.1em;
		height: 48px;
	}
}

/* Animation for error states */
.verification-input.is-invalid {
	animation: shake 0.5s ease-in-out;
}

@keyframes shake {

	0%,
	100% {
		transform: translateX(0);
	}

	25% {
		transform: translateX(-5px);
	}

	75% {
		transform: translateX(5px);
	}
}
</style>